import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/base/SEOHead";
import { getComicsByCategory } from "@/mocks/comics";
import { getCategoryBySlug } from "@/mocks/categories";
import { categories } from "@/mocks/categories";
import type { ComicArticle } from "@/mocks/comics";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || "");
  const comics = getComicsByCategory(slug || "");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [slug]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-300"></i>
          <h2 className="text-xl font-display text-foreground-700 mt-4">分类不存在</h2>
          <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">返回首页</Link>
        </div>
      </div>
    );
  }

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name}漫画 - 非麻瓜漫画`,
    description: category.description,
    url: `https://feimagua.com/category/${slug}`,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: comics.map((c: ComicArticle, i: number) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "ComicStory",
          name: c.title,
          url: `https://feimagua.com/article/${c.id}`,
        },
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`${category.name}漫画 - 非麻瓜漫画_免费韩漫在线阅读`}
        description={category.description}
        keywords={`${category.name}漫画,非麻瓜漫画,免费韩漫,韩国漫画,在线阅读`}
        canonicalUrl={`https://feimagua.com/category/${slug}`}
        schema={categorySchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: `${category.name}漫画` },
            ]}
          />

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                <i className={`${category.icon} text-xl text-primary-500`}></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-display text-foreground-950">
                  {category.name}漫画
                </h1>
                <p className="text-sm text-foreground-500">{category.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap mt-4 mb-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
              {comics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.id}`}
                  className="group cursor-pointer"
                  data-product-shop=""
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-2.5">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      title={`${comic.title} - ${category.name}漫画 免费韩漫在线阅读`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                        {comic.rating}
                      </span>
                    </div>
                    {comic.isHot && (
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-0.5 rounded-full bg-accent-500 text-background-50 text-xs font-medium">
                          HOT
                        </span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    {comic.author} · 更新至第{comic.chapters.length}话
                  </p>
                </Link>
              ))}
            </div>

            {comics.length === 0 && (
              <div className="text-center py-20">
                <i className="ri-book-open-line text-5xl text-foreground-300"></i>
                <p className="text-foreground-500 mt-4">该分类暂无漫画，敬请期待</p>
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}